import {Component, forwardRef, OnDestroy, OnInit, Renderer2, ViewChild,} from '@angular/core';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import {Select, Store} from '@ngxs/store';
import {combineLatest, Observable, Subject} from 'rxjs';
import {distinctUntilChanged, filter, map, take, takeUntil} from 'rxjs/operators';
import {
  BackLink,
  BackLinkState,
  OrganizationService,
  PostingDataResponseModel,
  DetailsState,
  GetInventoryItemDetails,
  CancelEditPosting,
  EditPostingState,
  SetAvailableVehicleTypes,
  SetInitialPosting,
  SetPostingType,
  SetTireFormsCertifiedOrValid,
  GetAuctionItem,
  DateUtilsService,
  EditPostingForms,
  NewPostingInspectionOption,
  PostingTypes,
  EditPostingService
} from 'remarketing-angular-library';
import {TiresFormService} from '../../services/tires-form.service';
import {PostingSuccessModalComponent} from '../posting-success-modal/posting-success-modal.component';
import { ModalComponent, SpinnerComponent } from 'remarketing-ui-components-lib';
import {CheckUnsavedPosts} from '../../unsaved-post.guard';
import {PostingFormTrackingService} from '../../services/posting-form-tracking.service';
import { TireValidationHelperService } from '../../services/tire-validation-helper.service';
import {PostingAnalyticsHelperService} from '../../services/posting-analytics-helper.service';
import { EditPostingUtilsService } from '../../services/edit-posting-utils.service';
import { SaComponent } from 'smartauction-frontend-micro-library';
import { TranslocoService } from '@jsverse/transloco';
import { EditPostingNavComponent } from '../edit-posting-nav/edit-posting-nav.component';
import { PostingSkeletonComponent } from '../posting-skeleton/posting-skeleton.component';
import { DescriptionFormComponent } from '../description-form/description-form.component';
import { LocationFormComponent } from '../location-form/location-form.component';
import { ExteriorFormComponent } from '../exterior-form/exterior-form.component';
import { InteriorFormComponent } from '../interior-form/interior-form.component';
import { MechanicalFormComponent } from '../mechanical-form/mechanical-form.component';
import {
  FullDescriptionFormComponent
} from '../full-description-form/full-description-form.component';
import { PhotosFormComponent } from '../photos-form/photos-form.component';
import { ConditionFormComponent } from '../condition-form/condition-form.component';
import { DamagesFormComponent } from '../damages-form/damages-form.component';
import { TiresFormComponent } from '../tires-form/tires-form.component';
import {
  AdditionalDisclosuresFormComponent
} from '../additional-disclosures-form/additional-disclosures-form.component';
import {
  PricingAndFeesFormComponent
} from '../pricing-and-fees-form/pricing-and-fees-form.component';
import {
  EditPostingButtonsComponent
} from '../edit-posting-buttons/edit-posting-buttons.component';
import { EditPostingMobileComponent } from '../edit-posting-mobile/edit-posting-mobile.component';
import { CommonModule, DatePipe } from '@angular/common';
import {
  OptionsAndAccessoriesFormsService
} from '../../services/options-and-accessories-forms.service';
import { PhotoUploadService } from '../../services/photo-upload.service';

@Component({
    selector: 'app-edit-posting',
    templateUrl: './edit-posting.component.html',
    styleUrls: ['./edit-posting.component.scss'],
    providers: [
        TiresFormService,
        OptionsAndAccessoriesFormsService,
        PhotoUploadService,
        DatePipe,
        { provide: CheckUnsavedPosts, useExisting: forwardRef(() => EditPostingComponent) }
    ],
    imports: [
        PostingSuccessModalComponent,
        ModalComponent,
        RouterLink,
        SpinnerComponent,
        EditPostingNavComponent,
        PostingSkeletonComponent,
        DescriptionFormComponent,
        LocationFormComponent,
        ExteriorFormComponent,
        InteriorFormComponent,
        MechanicalFormComponent,
        FullDescriptionFormComponent,
        PhotosFormComponent,
        ConditionFormComponent,
        DamagesFormComponent,
        TiresFormComponent,
        AdditionalDisclosuresFormComponent,
        PricingAndFeesFormComponent,
        EditPostingButtonsComponent,
        EditPostingMobileComponent,
        CommonModule
    ]
})
export class EditPostingComponent extends SaComponent implements OnInit, OnDestroy, CheckUnsavedPosts {
  @ViewChild(ModalComponent) unsavedChangesModal: ModalComponent;
  @Select(EditPostingState.selectedForm) selectedForm$: Observable<EditPostingForms>;
  @Select(EditPostingState.showSpinner) showSpinner$: Observable<boolean>;
  @Select(EditPostingState.skipNavigationGuard) skipNavigationGuard$: Observable<boolean>;

  selectedForm: EditPostingForms;
  selectedFormHeader: string;
  postingData: PostingDataResponseModel;
  disableLinksForHold = false;
  postingTypeString: string;
  analyticsPostingTypeString: string;
  repostBeforeAfterDayPeriod = 60;
  postingType: PostingTypes;
  showSpinner = false;
  skipNavigationGuard = false;
  updatedPostingVehicleDescription = '';
  isYellowLightChecked: boolean;
  private cancelEditing$: Subject<boolean> = new Subject<boolean>();
  modalHeading: string;
  modalContent: string;
  modalWarningContent: string;
  postingTypes = PostingTypes;
  selectedInspectionOption: NewPostingInspectionOption;
  backLinkTranslation: string;

  editPostingTranslations = {
    vehicleInformation: '',
    optionsAndAccessories: '',
    vehicleImages: '',
    vehicleDisclosures: '',
    vehiclePricing: '',
    editVehicle: '',
    repostVehicle: '',
    newPost: '',
    newThirdPartyPost: '',
    textVehiclePending: '',
    textWantToLeave: '',
    textLeaveNowNewPost: '',
    textLeaveNow: '',
    btnExit: '',
    modalClose: '',
    btnContinueEditing: '',
    btnGoBack: ''
  };

  @Select(BackLinkState.currentBackLink('remarketingLibrary.backLinks.backtoManageInventory', '/inventory'))
  backLink$: Observable<BackLink>;

  @ViewChild(PostingSuccessModalComponent) postingSuccess: PostingSuccessModalComponent;

  constructor(
    private readonly postingService: EditPostingService,
    private readonly router: Router,
    private readonly store: Store,
    private readonly route: ActivatedRoute,
    private readonly dateUtilService: DateUtilsService,
    private readonly organizationService: OrganizationService,
    private readonly postingFormTrackingService: PostingFormTrackingService,
    private readonly editPostingUtilsService: EditPostingUtilsService,
    private readonly renderer: Renderer2,
    private readonly tireValidationHelperService: TireValidationHelperService,
    private readonly postingAnalyticsHelperService: PostingAnalyticsHelperService,
    private readonly translocoService: TranslocoService
  ) {
    super();
  }

  ngOnInit() {
    this.postingType = this.route.snapshot.data?.postingType;
    this.getTranslations();

    this.showSpinner$.pipe(takeUntil(super.destroyed$)).subscribe(_showSpinner => {
      this.showSpinner = _showSpinner;
    });

    this.skipNavigationGuard$.pipe(takeUntil(super.destroyed$)).subscribe(skipGuard => {
      this.skipNavigationGuard = skipGuard;
    });

    if (this.postingType === PostingTypes.NEW) {
      this.initializeNewPosting();
      if (!this.store.selectSnapshot(EditPostingState.userHasStartedViewingRequiredPostingForms)) {
        this.postingFormTrackingService.initializeNewPostingFormTrackingOnState(this.selectedForm);
      }
    } else {
      this.initializeExistingPosting();
    }

    this.handleVehicleHeaderDescriptionSources();
  }

handleVehicleHeaderDescriptionSources(): void {
  combineLatest([
    this.translocoService.selectTranslate('common.notAvailable').pipe(
      filter(translate => !!translate)
    ),
    this.store.select(EditPostingState.updatedPosting)
  ]).pipe(
    map(([notAvailable, posting]) => {
      const description = posting?.inventoryItem?.vehicleInformation?.vehicleDescription;
      return description?.trim() ? description : notAvailable;
    }),
    distinctUntilChanged(),
    takeUntil(super.destroyed$)
  ).subscribe(description => {
    this.updatedPostingVehicleDescription = description;
  });
}

  setHeaders() {
    this.selectedForm$.pipe(takeUntil(super.destroyed$)).subscribe(selectedForm => {
      this.selectedForm = selectedForm;
      switch (this.selectedForm) {
        case EditPostingForms.OPTIONS_EXTERIOR:
        case EditPostingForms.OPTIONS_INTERIOR:
        case EditPostingForms.OPTIONS_MECHANICAL:
        case EditPostingForms.OPTIONS_ADDITIONAL:
          this.selectedFormHeader = this.editPostingTranslations.optionsAndAccessories;
          break;
        case EditPostingForms.IMAGES_PHOTOS:
          this.selectedFormHeader = this.editPostingTranslations.vehicleImages;
          break;
        case EditPostingForms.DISCLOSURES_CONDITION:
        case EditPostingForms.DISCLOSURES_DAMAGES:
        case EditPostingForms.DISCLOSURES_TIRES:
        case EditPostingForms.DISCLOSURES_ADDITIONAL:
          this.selectedFormHeader = this.editPostingTranslations.vehicleDisclosures;
          break;
        case EditPostingForms.PRICING_PRICING_AND_FEES:
          this.selectedFormHeader = this.editPostingTranslations.vehiclePricing;
          break;
        case EditPostingForms.INFO_LOCATION:
        case EditPostingForms.INFO_DESCRIPTION:
        default:
          this.selectedFormHeader = this.editPostingTranslations.vehicleInformation;
          break;
      }
    });
  }

  /**
   * Set initial values and retrieve required data for and EDIT or REPOST.
   */
  initializeExistingPosting() {
    const inventoryItemUuid = this.router.url.split('/').pop();
    this.postingService
      .getPostingData(inventoryItemUuid)
      .pipe(take(1))
      .subscribe(postingDataRes => {
        const postingData = this.editPostingUtilsService.validateInspectionReport(postingDataRes);
        this.setEditPostingTypeState(postingData.inventoryItem.postingInformation.postedDate);
        if (this.postingType === PostingTypes.REPOST_AFTER_60_DAYS) {
          this.fetchVinDecodeData(postingData);
        } else {
          this.setPostingData(postingData);
        }
        this.handleSettingValidationStateOfTireForm(postingData);
      });
    this.setsInventoryAndAuctionItem(inventoryItemUuid);
  }

  handleSettingValidationStateOfTireForm(postingData: PostingDataResponseModel): void {
    const tiresValid = this.tireValidationHelperService.handleTireValidationOnExistingPostingLoad(postingData);
    this.store.dispatch(new SetTireFormsCertifiedOrValid(tiresValid));
    this.tireValidationHelperService.setTiresCertifiedByUserOnExistingPostingLoad(postingData);
  }

  /**
   * Set initial values and get required data for a NEW posting.
   */
  initializeNewPosting() {
    this.selectedInspectionOption = this.store.selectSnapshot(EditPostingState.selectedInspectionOption);
    if (this.selectedInspectionOption === NewPostingInspectionOption.WAIT_FOR_INSPECTION) {
      this.disableLinksForHold = true;
    }
    this.store.dispatch(new SetPostingType(PostingTypes.NEW));
    const vehicleTypes = this.store.selectSnapshot(EditPostingState.vehicleTypes);
    const sellerOeUuid = this.store.selectSnapshot(EditPostingState.originalPosting).sellerOeUuid;
    if (!vehicleTypes || vehicleTypes?.length <= 0) {
      this.organizationService
        .getAvailableVehicleTypesForSeller(this.editPostingUtilsService.getSelectedDealershipUuid(sellerOeUuid))
        .pipe(take(1))
        .subscribe(response => {
          // Response does not include the selected property and 2.0 will throw errors if not present.
          response.forEach(vehicleType => (vehicleType.selected = false));
          this.store.dispatch(new SetAvailableVehicleTypes(response));
        });
    }
    this.postingData = this.store.selectSnapshot(EditPostingState.originalPosting);
    this.setTireFormsAsInvalid();
  }

  setTireFormsAsInvalid(): void {
    this.store.dispatch(new SetTireFormsCertifiedOrValid(false));
  }

  ngOnDestroy() {
    this.store.dispatch(new CancelEditPosting());
    super.destroyed$.next();
    super.destroyed$.complete();
  }

  showNavigationAlert(): Observable<boolean> | boolean {
    if (this.skipNavigationGuard) {
      return true;
    } else {
      this.unsavedChangesModal.show();
      this.postingAnalyticsHelperService.trackModalView('unsavedChanges');
      this.cancelEditing$ = new Subject<boolean>();
      return this.cancelEditing$.asObservable();
    }
  }

  continueEditing() {
    this.unsavedChangesModal.close();
    this.cancelEditing$.next(false);
    this.cancelEditing$.complete();
  }

  cancelEditing() {
    this.unsavedChangesModal.close();
    this.cancelEditing$.next(true);
    this.cancelEditing$.complete();
  }

  /**
   * set inventory and auction item respective to selected vehicle in its respective states
   * @param inventoryItemUuid
   */
  setsInventoryAndAuctionItem(inventoryItemUuid) {
    this.store
      .dispatch(
        new GetInventoryItemDetails(inventoryItemUuid, this.editPostingUtilsService.getSelectedDealershipUuid())
      )
      .pipe(take(1))
      .subscribe(() => {
        const inventoryItem = this.store.selectSnapshot(DetailsState.inventoryItem);
        this.store.dispatch(new GetAuctionItem(inventoryItem.auctionItemUuid));
      });
  }

  vehicleHeaderHelper() {
    // update to the most recent state
    this.store
      .select(EditPostingState.updatedPosting)
      .pipe(takeUntil(super.destroyed$))
      .subscribe(postingData => {
        this.postingData = postingData;
      });
  }

  get vehicleVin() {
    return this.postingData && this.postingData.inventoryItem && this.postingData.inventoryItem.vehicleInformation
      ? this.postingData.inventoryItem.vehicleInformation.vin
      : '';
  }

  get editPostingForms(): typeof EditPostingForms {
    return EditPostingForms;
  }

  isMobile(): boolean {
    return window.matchMedia('(max-width: 768px)').matches;
  }

  setEditPostingTypeState(postedDate: string) {
    switch (this.postingType) {
      case PostingTypes.NEW:
      case PostingTypes.EDIT:
        this.store.dispatch(new SetPostingType(this.postingType));
        break;
      case PostingTypes.REPOST_BEFORE_60_DAYS:
      case PostingTypes.REPOST_AFTER_60_DAYS:
        this.postingType = this.getRepostPostingType(postedDate);
        this.store.dispatch(new SetPostingType(this.postingType));
        break;
    }
  }

  setPostingFormValues() {
    switch (this.postingType) {
      case PostingTypes.EDIT:
        this.postingTypeString = this.editPostingTranslations.editVehicle;
        this.analyticsPostingTypeString = 'Edit Vehicle';
        break;
      case PostingTypes.REPOST_AFTER_60_DAYS:
      case PostingTypes.REPOST_BEFORE_60_DAYS:
        this.postingTypeString = this.editPostingTranslations.repostVehicle;
        this.analyticsPostingTypeString = 'Repost Vehicle';
        break;
      case PostingTypes.NEW:
        this.postingTypeString = this.isNewThirdPartyPosting()
          ? this.editPostingTranslations.newThirdPartyPost
          : this.editPostingTranslations.newPost;
        this.analyticsPostingTypeString = this.isNewThirdPartyPosting() ? 'New Third-party Post' : 'New Post';
        break;
    }
  }

  isNewThirdPartyPosting(): boolean {
    const isThirdParty = this.store.selectSnapshot(EditPostingState.updatedPosting)?.inventoryItem?.postingInformation
      ?.thirdPartyPosting;
    return !!isThirdParty && this.postingType === PostingTypes.NEW;
  }

  getRepostPostingType(postedDate: string) {
    const datePosted = new Date(postedDate);
    const differenceInDays = this.dateUtilService.getDifferenceInDaysBetweenTwoDates(datePosted, new Date());
    return differenceInDays <= this.repostBeforeAfterDayPeriod
      ? PostingTypes.REPOST_BEFORE_60_DAYS
      : PostingTypes.REPOST_AFTER_60_DAYS;
  }

  setPostingData(postingResponse: PostingDataResponseModel) {
    this.postingData = postingResponse;
    this.disableLinksForHold =
      this.postingData.inventoryItem.itemStatus.status === 'HOLD' &&
      this.postingData.inventoryItem.itemStatus.statusReasons.filter(
        e => e.reasonDescription === 'Pending Batch Inspection' && e.statusCleared === false
      ).length > 0;
    this.store.dispatch(new SetInitialPosting(this.postingData));
  }

  fetchVinDecodeData(postingResponse: PostingDataResponseModel) {
    this.postingService
      .getVinDecodeDetails(postingResponse.inventoryItem.vehicleInformation.vin, false)
      .pipe(take(1))
      .subscribe(vinDecodeRes => {
        postingResponse.inventoryItem.vehicleInformation.make = vinDecodeRes.vehicleInformation.make;
        postingResponse.inventoryItem.vehicleInformation.model = vinDecodeRes.vehicleInformation.model;
        postingResponse.inventoryItem.vehicleInformation.series = vinDecodeRes.vehicleInformation.series;
        this.setPostingData(postingResponse);
      });
  }

  yellowLightSelected($event) {
    this.isYellowLightChecked = $event;
  }

  showPostingSuccessModal(event) {
    this.modalContent = event?.content;
    this.modalHeading = event?.header;
    this.modalWarningContent = event?.warningContent;
    this.postingSuccess.show();
    this.focusPostingSuccessModal();
    this.postingAnalyticsHelperService.trackPostSuccess(
      'postingSuccess',
      this.postingData.inventoryItem.vehicleInformation.vin
    );
  }

  focusPostingSuccessModal() {
    setTimeout(() => this.renderer.selectRootElement('.title', true).focus());
  }

  getTranslations(): void {
    const keyParams = {
      'sellModule.posting.editPostingHeaders': {},
      'sellModule.posting.editPostingTitles': {},
      'sellModule.posting.editPosting': {},
      common: {}
    };
    this.translocoService
      .selectTranslateObject(keyParams)
      .pipe(takeUntil(super.destroyed$))
      .subscribe(translate => {
        this.editPostingTranslations.vehicleInformation = translate[0].vehicleInformation;
        this.editPostingTranslations.optionsAndAccessories = translate[0].optionsAndAccessories;
        this.editPostingTranslations.vehicleImages = translate[0].vehicleImages;
        this.editPostingTranslations.vehicleDisclosures = translate[0].vehicleDisclosures;
        this.editPostingTranslations.vehiclePricing = translate[0].vehiclePricing;

        this.editPostingTranslations.editVehicle = translate[1].editVehicle;
        this.editPostingTranslations.repostVehicle = translate[1].repostVehicle;
        this.editPostingTranslations.newPost = translate[1].newPost;
        this.editPostingTranslations.newThirdPartyPost = translate[1].newThirdPartyPost;
        this.editPostingTranslations.textVehiclePending = translate[2].textVehiclePending;
        this.editPostingTranslations.textWantToLeave = translate[2].textWantToLeave;
        this.editPostingTranslations.textLeaveNowNewPost = translate[2].textLeaveNowNewPost;
        this.editPostingTranslations.textLeaveNow = translate[2].textLeaveNow;
        this.editPostingTranslations.btnExit = translate[2].btnExit;
        this.editPostingTranslations.modalClose = translate[3].modalClose;
        this.editPostingTranslations.btnContinueEditing = translate[3].btnContinueEditing;
        this.editPostingTranslations.btnGoBack = translate[3].btnGoBack;
        this.setHeaders();
        this.setPostingFormValues();
      });
  }

  getBackLinkTranslation(key: string): string {
    let backLinkText = '';
    this.translocoService
      .selectTranslate(key)
      .pipe(take(1))
      .subscribe(translation => {
        backLinkText = translation;
      });
    return backLinkText;
  }
}
